<template>
  <div class="consultant-detail-card">
      <!-- Header -->
      <div class="card-header">
        <h2 class="card-title clickable" @click="openBusinessDetails">{{ t('ccConsultantDetail.ConsultantDetails') }}

                            <SvgIcon class="check-mark-outline-icon" :icon="infoCircleIcon">
          </SvgIcon>
        </h2>

        <div class="header-actions">
           <button
            v-if="props.reportId && !loading && !error"
            class="bookmark-button k-button k-button-md k-button-flat k-button-icon"
            @click="toggleBookmark"
            :aria-label="isBookmarked ? t('ccConsultantDetail.RemoveBookmark') : t('ccConsultantDetail.AddBookmark')"
            :title="isBookmarked ? t('ccConsultantDetail.RemoveBookmark') : t('ccConsultantDetail.AddBookmark')"
          >
            <span class="k-icon" :class="isBookmarked ? 'k-i-bookmark' : 'k-i-bookmark-outline'"></span>
          </button>
          <button class="close-button" @click="$emit('close')" :aria-label="t('buttons.Close')">
            <span class="k-icon k-i-close">×</span>
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="card-loading">
        <Loader />
        <p class="loading-text">{{ t('ccConsultantDetail.LoadingDetails') }}</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="card-error">
        <div class="error-icon">⚠️</div>
        <p class="error-message">{{ error }}</p>
        <button class="retry-button k-button k-primary" @click="loadConsultantDetails">
          {{ t('ccConsultantDetail.Retry') }}
        </button>
      </div>

      <!-- Content -->
      <div v-else class="card-content">
        <!-- Profile Photo -->
        <div class="profile-photo-section">
          <img :src="displayPhotoUrl" :alt="t('ccConsultantDetail.ProfilePhotoAlt')" class="profile-photo" />
        </div>

        <!-- Full Name -->
        <div class="detail-row">
          <div class="detail-label">{{ t('ccConsultantDetail.FullName') }}:</div>
          <div class="detail-value">{{ fullName }}</div>
        </div>

        <!-- Status -->
        <div class="detail-row">
          <div class="detail-label">{{ t('ccConsultantDetail.Status') }}:</div>
          <div class="detail-value">{{ consultantData.status || t('ccConsultantDetail.NotAvailable') }}</div>
        </div>

        <!-- Address -->
        <div class="detail-row address-row">
          <div class="detail-label">{{ t('ccConsultantDetail.Address') }}:</div>
          <div class="detail-value">
            <div v-if="address" class="address-lines">
              <div v-if="address.addressLine1">{{ address.addressLine1 }}</div>
              <div v-if="address.addressLine2">{{ address.addressLine2 }}</div>
              <div v-if="address.city || address.stateCode || address.postalCode">
                {{ [address.city, address.stateCode && address.stateCode !== 'N/A' ? address.stateCode : null, address.postalCode].filter(Boolean).join(', ') }}
              </div>
              <div v-if="address.countryCode">{{ address.countryCode }}</div>
            </div>
            <div v-else class="no-data">{{ t('ccConsultantDetail.NoAddressAvailable') }}</div>
          </div>
        </div>

        <!-- Phone -->
        <div v-if="!props.hideExtraFields" class="detail-row">
          <div class="detail-label">{{ t('ccConsultantDetail.Phone') }}:</div>
          <div class="detail-value">{{ consultantData.phone || t('ccConsultantDetail.NotAvailable') }}</div>
        </div>

        <!-- Email -->
        <div class="detail-row">
          <div class="detail-label">{{ t('ccConsultantDetail.Email') }}:</div>
          <div class="detail-value">
            <a v-if="consultantData.email" :href="`mailto:${consultantData.email}`" class="email-link">
              {{ consultantData.email }}
            </a>
            <span v-else>{{ t('ccConsultantDetail.NotAvailable') }}</span>
          </div>
        </div>

        <!-- Sponsor -->
        <div v-if="!props.hideExtraFields" class="detail-row">
          <div class="detail-label">{{ t('ccConsultantDetail.Sponsor') }}:</div>
          <div class="detail-value">{{ sponsor }}</div>
        </div>

        <!-- Recruiter -->
        <div v-if="!props.hideExtraFields" class="detail-row">
          <div class="detail-label">{{ t('ccConsultantDetail.Recruiter') }}:</div>
          <div class="detail-value">{{ recruiter }}</div>
        </div>

        <!-- Personal Group -->
        <div v-if="!props.hideExtraFields" class="detail-row">
          <div class="detail-label">{{ t('ccConsultantDetail.PersonalGroup') }}:</div>
          <div class="detail-value">{{ personalGroup }}</div>
        </div>

        <!-- Consultant Search -->
        <div v-if="!props.hideExtraFields && props.consultantList && props.consultantList.length > 0" class="detail-row search-row">
          <div class="detail-label">{{ t('ccConsultantDetail.SearchConsultant') }}:</div>
          <div class="detail-value">
            <AutoComplete
              v-model="searchValue"
              :data-items="filteredConsultantList"
              :data-item-key="'beeNumber'"
              :text-field="'name'"
              :placeholder="t('ccConsultantDetail.SearchPlaceholder')"
              :filterable="false"
              @select="handleConsultantSelect"
              class="consultant-search"
            />
          </div>
        </div>

        <!-- Add to Bookmarks -->
        <div v-if="props.reportId" class="detail-row bookmark-row">
          <div class="detail-label">{{ t('ccConsultantDetail.Bookmark') }}:</div>
          <div class="detail-value">
            <button
              class="k-button k-button-md add-bookmark-button"
              :class="isBookmarked ? 'k-button-solid-primary' : 'k-button-outline-primary'"
              @click="toggleBookmark"
            >
              <span class="k-icon" :class="isBookmarked ? 'k-i-bookmark' : 'k-i-bookmark-outline'"></span>
              {{ isBookmarked ? t('ccConsultantDetail.RemoveBookmark') : t('ccConsultantDetail.AddBookmark') }}
            </button>
          </div>
        </div>

        <!-- Bookmarked Consultants -->
        <div v-if="props.reportId" class="detail-row bookmarked-list-row">
          <div class="detail-label">{{ t('ccConsultantDetail.BookmarkedConsultants') }}:</div>
          <div class="detail-value">
            <div v-if="bookmarksLoading" class="bookmarks-loading">
              <Loader size="small" />
            </div>
            <ul v-else-if="bookmarkedConsultants.length > 0" class="bookmarked-list">
              <li v-for="item in bookmarkedConsultants" :key="item.sfaTeamDetailsBookmarkGuid" class="bookmarked-item">
                <span
                  class="bookmarked-name"
                  :class="{ clickable: isConsultantSelectable(item) }"
                  @click="selectBookmarkedConsultant(item)"
                >{{ item.name }}</span>
                <button
                  class="remove-bookmark-button"
                  :aria-label="t('ccConsultantDetail.RemoveBookmark')"
                  :title="t('ccConsultantDetail.RemoveBookmark')"
                  @click="removeBookmarkedConsultant(item)"
                >
                  <span class="k-icon k-i-close"></span>
                </button>
              </li>
            </ul>
            <div v-else class="no-data">{{ t('ccConsultantDetail.NoBookmarkedConsultants') }}</div>
          </div>
        </div>
      </div>
  </div>

  <!-- BusinessDetails Popup -->
  <Window
    v-if="showBusinessDetails"
    class="business-details-window"
    :title="t('ccConsultantDetail.BusinessDetails')"
    :modal="true"
    :maximize-button="true"
    :width="windowWidth"
    :height="windowHeight"

    @close="showBusinessDetails = false"
  >
    <BusinessDetails
      :beeNumber="props.beeNumber ? parseInt(props.beeNumber, 10) : undefined"
      @close="showBusinessDetails = false"
    />
  </Window>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { Loader } from '@progress/kendo-vue-indicators'
import { Window } from '@progress/kendo-vue-dialogs'
import { AutoComplete } from '@progress/kendo-vue-dropdowns'
import { infoCircleIcon } from '@progress/kendo-svg-icons'
  import { SvgIcon } from '@progress/kendo-vue-common'
import { PwsProfileBeeUplineLevelGet, type AddressModel, PwsProfilePhotoGet } from '@/api/ConsultantCorner'
import { PwsProfileAddressGet } from '@/api/AccountInfo'
import {
  ConsultantBookmarkAdd,
  ConsultantBookmarkRemove,
  ConsultantBookmarkCheck,
  ConsultantBookmarkGetList,
  type ConsultantBookmarkAddRequest,
  type ConsultantBookmarkRemoveRequest,
  type ConsultantBookmarkCheckRequest,
  type IConsultantBookmark
} from '@/api/ConsultantBookmark'
import { useI18n } from 'vue-i18n'
import { GetImageRootUrl } from '@/stores/SettingsStore'
import BusinessDetails from '@/components/BusinessDetails.vue'

const { t } = useI18n()

interface ConsultantOption {
  beeNumber: string
  name: string
}

interface Props {
  isOpen: boolean
  beeNumber: string | null
  consultantName: string
  status: string
  phone: string
  email: string
  cardTop: number
  hideExtraFields?: boolean
  consultantList?: ConsultantOption[]
  reportId?: string
}

const props = defineProps<Props>()
const emit = defineEmits<{
  close: []
  'select-consultant': [beeNumber: string]
}>()

const loading = ref(false)
const error = ref('')
const address = ref<AddressModel | null>(null)
const sponsorFirstName = ref('')
const sponsorLastName = ref('')
const recruiterFirstName = ref('')
const recruiterLastName = ref('')
const personalGroupFirstName = ref('')
const personalGroupLastName = ref('')
const profilePhotoUrl = ref('')
const imageRootUrl = ref('')
const showBusinessDetails = ref(false)
const windowWidth = ref(1200)
const windowHeight = ref(800)
const searchValue = ref('')
const isBookmarked = ref(false)
const beeNumberBookmark = computed(() => props.beeNumber ? parseInt(props.beeNumber, 10) : null)
const bookmarkedConsultants = ref<IConsultantBookmark[]>([])
const bookmarksLoading = ref(false)

// Filtered consultant list based on search input
const filteredConsultantList = computed(() => {
  if (!searchValue.value || !props.consultantList) {
    return props.consultantList || []
  }

  const searchTerm = searchValue.value.toLowerCase()
  return props.consultantList.filter(consultant =>
    consultant.name.toLowerCase().includes(searchTerm)
  )
})

onMounted(async () => {
  const isMobile = window.innerWidth < 768
  windowWidth.value = isMobile ? window.innerWidth - 20 : Math.floor(window.innerWidth * 0.9)
  windowHeight.value = isMobile ? window.innerHeight - 20 : Math.floor(window.innerHeight * 0.9)
  imageRootUrl.value = await GetImageRootUrl()
})

const consultantData = computed(() => ({
  name: props.consultantName,
  status: props.status,
  phone: props.phone,
  email: props.email
}))

const fullName = computed(() => consultantData.value.name || t('ccConsultantDetail.NotAvailable'))

const sponsor = computed(() => {
  if (sponsorFirstName.value || sponsorLastName.value) {
    return `${sponsorFirstName.value} ${sponsorLastName.value}`.trim()
  }
  return t('ccConsultantDetail.NotAvailable')
})

const recruiter = computed(() => {
  if (recruiterFirstName.value || recruiterLastName.value) {
    return `${recruiterFirstName.value} ${recruiterLastName.value}`.trim()
  }
  return t('ccConsultantDetail.NotAvailable')
})

const personalGroup = computed(() => {
  if (personalGroupFirstName.value || personalGroupLastName.value) {
    return `${personalGroupFirstName.value} ${personalGroupLastName.value}`.trim()
  }
  return t('ccConsultantDetail.NotAvailable')
})

const displayPhotoUrl = computed(() => {
  if (profilePhotoUrl.value) {
    return profilePhotoUrl.value
  }
  return imageRootUrl.value + '/default-consultant-photo.png'
})

// Card position style no longer needed as it's now a static card

const getStatusClass = (status: string): string => {
  if (!status) return ''
  const statusLower = status.toLowerCase()
  if (statusLower.includes('active')) return 'status-active'
  if (statusLower.includes('inactive')) return 'status-inactive'
  if (statusLower.includes('pending')) return 'status-pending'
  return ''
}

const handleOverlayClick = () => {
  emit('close')
}

const openBusinessDetails = () => {
  showBusinessDetails.value = true
}

const handleConsultantSelect = (event: any) => {
  // Check if this is a selection event (has item) or just typing
  if (event.item) {
    const selected = event.item as ConsultantOption
    if (selected && selected.beeNumber) {
      emit('select-consultant', selected.beeNumber)
      searchValue.value = ''
    }
  }
}

const loadConsultantDetails = async () => {
  if (!props.beeNumber) {
    error.value = 'No BeeNumber provided'
    return
  }

  loading.value = true
  error.value = ''
  address.value = null
  sponsorFirstName.value = ''
  sponsorLastName.value = ''
  recruiterFirstName.value = ''
  recruiterLastName.value = ''
  personalGroupFirstName.value = ''
  personalGroupLastName.value = ''
  profilePhotoUrl.value = ''

  try {
    const beeNumberInt = parseInt(props.beeNumber, 10)

    // Load address, upline, and photo information in parallel
    const [addressResponse, uplineResponse, photoResponse] = await Promise.all([
      PwsProfileAddressGet({ beeNumber: beeNumberInt }).catch(err => {
        console.error('Error loading address:', err)
        return null
      }),
      PwsProfileBeeUplineLevelGet(beeNumberInt).catch(err => {
        console.error('Error loading upline:', err)
        return null
      }),
      PwsProfilePhotoGet({ beeNumber: beeNumberInt }).catch(err => {
        console.error('Error loading photo:', err)
        return null
      })
    ])

      // Extract address from profile
      if (addressResponse?.item) {
        const item = addressResponse.item
        let sourceAddress: any = null

        // Try to get shipping address first, then mailing, then billing
        if (item.shippingAddress?.address) {
          sourceAddress = item.shippingAddress.address
        } else if (item.mailingAddress?.address) {
          sourceAddress = item.mailingAddress.address
        } else if (item.billingAddress?.address) {
          sourceAddress = item.billingAddress.address
        } else if (item.allAddresses && item.allAddresses.length > 0) {
          // Use the first address from allAddresses array if no specific address type is available
          sourceAddress = item.allAddresses[0].address
        }

        // Map IAddress to AddressModel (required fields vs optional)
        if (sourceAddress) {
          address.value = {
            addressLine1: sourceAddress.addressLine1 || '',
            addressLine2: sourceAddress.addressLine2,
            city: sourceAddress.city || '',
            stateCode: sourceAddress.stateCode || '',
            postalCode: sourceAddress.postalCode || '',
            countryCode: sourceAddress.countryCode || '',
            firstName: sourceAddress.firstName,
            lastName: sourceAddress.lastName,
            houseNumber: sourceAddress.houseNumber
          }
        }
      }

      // Extract upline information
      if (uplineResponse) {
        sponsorFirstName.value = uplineResponse.item.sponsorFirstName || ''
        sponsorLastName.value = uplineResponse.item.sponsorLastName || ''
        recruiterFirstName.value = uplineResponse.item.recruiterFirstName || ''
        recruiterLastName.value = uplineResponse.item.recruiterLastName || ''
        personalGroupFirstName.value = uplineResponse.item.personalGroupFirstName || ''
        personalGroupLastName.value = uplineResponse.item.personalGroupLastName || ''
      }

      // Extract profile photo
      if (photoResponse?.approvedURI) {
        profilePhotoUrl.value = photoResponse.approvedURI
      }

      // Check bookmark status for this card
      await checkBookmarkStatus()

  } catch (err: any) {
    console.error('Error loading consultant details:', err)
    error.value = err.message || 'Failed to load consultant details'
  } finally {
    loading.value = false
  }
}

async function checkBookmarkStatus() {
  if (!props.reportId || !beeNumberBookmark.value) return

  try {
    const request: ConsultantBookmarkCheckRequest = {
      BeeNumberBookmark: beeNumberBookmark.value,
      ReportId: props.reportId
    }
    const response = await ConsultantBookmarkCheck(request)
    isBookmarked.value = response.isBookmarked
  } catch (err) {
    console.error('Error checking bookmark status:', err)
    isBookmarked.value = false
  }
}

async function toggleBookmark() {
  if (!props.reportId || !beeNumberBookmark.value || !props.beeNumber){
    console.log('missing something to toggle bookmark', { reportId: props.reportId, beeNumberBookmark: beeNumberBookmark.value, beeNumber: props.beeNumber })
    return
  }

  try {
    if (isBookmarked.value) {
      const request: ConsultantBookmarkRemoveRequest = {
        BeeNumberBookmark: beeNumberBookmark.value,
        ReportId: props.reportId
      }
      await ConsultantBookmarkRemove(request)
      isBookmarked.value = false
    } else {
      const request: ConsultantBookmarkAddRequest = {
        BeeNumberBookmark: beeNumberBookmark.value,
        Name: props.consultantName,
        ReportId: props.reportId
      }
      await ConsultantBookmarkAdd(request)
      isBookmarked.value = true
    }
    await loadBookmarkedConsultants()
  } catch (err) {
    console.error('Error toggling bookmark:', err)
  }
}

async function loadBookmarkedConsultants() {
  if (!props.reportId) {
    bookmarkedConsultants.value = []
    return
  }

  bookmarksLoading.value = true
  try {
    const response = await ConsultantBookmarkGetList({ ReportId: props.reportId })
    bookmarkedConsultants.value = response.items || []
  } catch (err) {
    console.error('Error loading bookmarked consultants:', err)
    bookmarkedConsultants.value = []
  } finally {
    bookmarksLoading.value = false
  }
}

function isConsultantSelectable(item: IConsultantBookmark): boolean {
  return !!props.consultantList?.some(consultant => consultant.name === item.name)
}

function selectBookmarkedConsultant(item: IConsultantBookmark) {
  const match = props.consultantList?.find(consultant => consultant.name === item.name)
  if (match) {
    emit('select-consultant', match.beeNumber)
  }
}

async function removeBookmarkedConsultant(item: IConsultantBookmark) {
  if (!props.reportId) return

  try {
    await ConsultantBookmarkRemove({
      BeeNumberBookmark: item.beeNumberBookmark,
      ReportId: props.reportId
    })
    await loadBookmarkedConsultants()
    if (beeNumberBookmark.value === item.beeNumberBookmark) {
      isBookmarked.value = false
    }
  } catch (err) {
    console.error('Error removing bookmarked consultant:', err)
  }
}

// Watch for beeNumber changes and load details
watch(() => props.beeNumber, (newBeeNumber) => {
  if (props.isOpen && newBeeNumber) {
    loadConsultantDetails()
  }
}, { immediate: true })

// Watch for beeNumber or reportId changes to check bookmark status
watch([() => props.beeNumber, () => props.reportId], () => {
  if (props.beeNumber && props.reportId) {
    checkBookmarkStatus()
  } else {
    isBookmarked.value = false
  }
})

// Watch for reportId changes to load the bookmarked consultants list
watch(() => props.reportId, () => {
  loadBookmarkedConsultants()
}, { immediate: true })
</script>

<style lang="scss" scoped>
.consultant-detail-card {
  background: white;
  width: 100%;
  border-radius: $radius-lg;
  box-shadow: 0 4px 16px rgba(black, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #e0e0e0;
}

.card-header {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--vt-c-white-soft);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  color: #333;
  flex-shrink: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: $spacing-2;
}

.bookmark-button {
  color: var(--color-text);
  &:hover {
    color: var(--color-teal);
  }
}

.card-title {
  @include typography-body;
  @include font-weight-semibold;
  margin: 0;
  color: var(--vt-c-teal-medium);
}

.card-title.clickable {
  cursor: pointer;
  transition: color 0.2s ease;
}

.card-title.clickable:hover {
  color: var(--vt-c-teal-dark);
  text-decoration: underline;
}

.close-button {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  @include typography-h4;
  padding: 0.5rem;
  line-height: 1;
  border-radius: $radius-md;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button:hover {
  background-color: var(--vt-c-white-soft);
  color: #333;
}

.card-loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 2rem;
}

.loading-text {
  @include typography-body;
  margin: 0;
  color: #666;
}

.card-error {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 2rem;
  text-align: center;
}

.error-icon {
  @include typography-display;
}

.error-message {
  @include typography-body;
  margin: 0;
  color: #dc3545;
}

.retry-button {
  margin-top: 0.5rem;
}

.card-content {
  padding: 1rem 1.25rem;
  max-height: 600px;
  overflow-y: auto;
}

.profile-photo-section {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
}

.profile-photo {
  width: 100px;
  height: 100px;
  border-radius: $radius-circle;
  object-fit: cover;
  border: 2px solid var(--vt-c-white-soft);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Form-style layout: label and value in same row */
.detail-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--vt-c-white-soft);
}

.detail-row:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

/* Address row needs special handling for multiline content */
.detail-row.address-row {
  align-items: flex-start;
}

.detail-row.search-row {
  align-items: center;
}

.detail-label {
  @include typography-small;
  @include font-weight-semibold;
  color: #555;
  min-width: 90px;
  flex-shrink: 0;
  padding-top: 2px;
}

.detail-value {
  @include typography-small;
  color: #333;
  line-height: 1.5;
  flex: 1;
  word-break: break-word;
}

.address-lines div {
  @include typography-small;
  margin-bottom: 0.125rem;
}

.address-lines div:last-child {
  margin-bottom: 0;
}

.no-data {
  color: #999999;
  font-style: italic;
}

.detail-row.bookmark-row,
.detail-row.bookmarked-list-row {
  align-items: center;
}

.add-bookmark-button {
  display: flex;
  align-items: center;
  gap: $spacing-1;
}

.bookmarks-loading {
  display: flex;
  align-items: center;
}

.bookmarked-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: $spacing-1;
}

.bookmarked-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $spacing-2;
  padding: $spacing-1 $spacing-2;
  background-color: var(--color-background-soft);
  border-radius: $radius-sm;
}

.bookmarked-name {
  @include typography-small;
  color: #333;
}

.bookmarked-name.clickable {
  cursor: pointer;
  color: var(--vt-c-teal-medium);
}

.bookmarked-name.clickable:hover {
  color: var(--vt-c-teal-dark);
  text-decoration: underline;
}

.remove-bookmark-button {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
}

.remove-bookmark-button:hover {
  color: #dc3545;
}

/* BusinessDetails Window override for full-screen sizing */
.detail-card-container:has(.k-window){
  width: 90vw !important;
  height: 90vh !important;
}

.email-link {
  color: var(--vt-c-teal-medium);
  text-decoration: none;
  transition: color 0.2s ease;
}

.email-link:hover {
  color: var(--vt-c-teal-dark);
  text-decoration: underline;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  @include typography-small;
  @include font-weight-medium;
  border-radius: $radius-lg;
  text-transform: capitalize;
}

.status-active {
  background-color: #d4edda;
  color: #155724;
}

.status-inactive {
  background-color: #f8d7da;
  color: #721c24;
}

.status-pending {
  background-color: #fff3cd;
  color: #856404;
}

/* Scrollbar styling */
.card-content::-webkit-scrollbar {
  width: 8px;
}

.card-content::-webkit-scrollbar-track {
  background: #fefefe;
}

.card-content::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: $radius-sm;
}

.card-content::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Responsive */
@media (max-width: 768px) {
  .consultant-detail-card {
    border-radius: $radius-md;
  }
}
</style>
